import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Paper,
  Popover,
  Typography,
  Button,
  Alert,
  alpha,
  MenuItem,
  Skeleton,
} from '@mui/material';
import {
  Refresh,
  AccountBalanceWallet,
  CheckRounded,
  KeyboardArrowDownRounded,
} from '@mui/icons-material';
import PortfolioTradingAPI from '../../services/portfolioTradingAPI';
import ModelPortfolioBoard from './ModelPortfolioBoard';
import ModelPortfolioActions from './ModelPortfolioActions';

const surfaceSx = {
  borderRadius: 3,
  border: '1px solid #e2e8f0',
  bgcolor: '#ffffff',
  boxShadow: 'none',
};

const headerControlSx = {
  height: 40,
  minHeight: 40,
  borderRadius: '4px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: 13,
  color: '#64748b',
  borderColor: '#e2e8f0',
  alignItems: 'center',
  '&:hover': {
    bgcolor: '#f8fafc',
    borderColor: '#cbd5e1',
  },
};

function LoadingState() {
  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Paper elevation={0} sx={{ ...surfaceSx, p: 2.5 }}>
          <Skeleton width={220} height={32} />
          <Skeleton width={420} height={18} sx={{ mt: 0.5 }} />
          <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
            <Skeleton variant="rounded" width={250} height={40} sx={{ borderRadius: 1.5 }} />
            <Skeleton variant="rounded" width={120} height={40} sx={{ borderRadius: 1.5 }} />
          </Box>
        </Paper>
        <Paper elevation={0} sx={{ ...surfaceSx, p: 2.5 }}>
          <Skeleton width="100%" height={420} />
        </Paper>
      </Box>
    </Container>
  );
}

export default function PaperTradingDashboard() {
  const { t } = useTranslation();
  const [portfolios, setPortfolios] = useState([]);
  const [portfolioId, setPortfolioId] = useState('');
  const [portfolio, setPortfolio] = useState(null);
  const [modelBoard, setModelBoard] = useState(null);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portfolioMenuAnchor, setPortfolioMenuAnchor] = useState(null);

  const selectedPortfolio = portfolios.find((item) => item.id === portfolioId) || null;

  const loadPortfolios = useCallback(async () => {
    try {
      const data = await PortfolioTradingAPI.getAllPortfolios();
      setPortfolios(data);
      if (data.length > 0 && !portfolioId) {
        setPortfolioId(data[0].id);
      }
      if (data.length === 0) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error loading portfolios:', err);
      setError(t('paperTrading.errors.loadPortfolios'));
      setLoading(false);
    }
  }, [portfolioId, t]);

  const loadBoard = useCallback(async () => {
    if (!portfolioId) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      try {
        await PortfolioTradingAPI.refreshPositionPrices(portfolioId);
      } catch (err) {
        // Best-effort refresh only.
      }

      const [portfolioData, boardData, positionsData] = await Promise.all([
        PortfolioTradingAPI.getPortfolio(portfolioId),
        PortfolioTradingAPI.getModelBoard(portfolioId, 180, 50),
        PortfolioTradingAPI.getPositions(portfolioId),
      ]);

      setPortfolio(portfolioData);
      setModelBoard(boardData);
      setPositions(positionsData);
    } catch (err) {
      console.error('Error loading model portfolio board:', err);
      setError(err.message || t('paperTrading.errors.loadBoard'));
    } finally {
      setLoading(false);
    }
  }, [portfolioId, t]);

  useEffect(() => {
    loadPortfolios();
  }, [loadPortfolios]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  if (loading && !modelBoard) {
    return <LoadingState />;
  }

  return (
    <Box sx={{ p: 2.5, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
            <Box sx={{
              width: 42,
              height: 42,
              borderRadius: 1.5,
              flexShrink: 0,
              background: 'linear-gradient(135deg, #0f766e 0%, #0ea5e9 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 3px 10px ${alpha('#0ea5e9', 0.28)}`,
            }}>
              <AccountBalanceWallet sx={{ fontSize: 22, color: '#fff' }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
                {t('paperTrading.modelPortfolioTitle')}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500, lineHeight: 1.3, mt: 0.3, maxWidth: 760 }}>
                {t('paperTrading.modelPortfolioSubtitle')}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={(event) => setPortfolioMenuAnchor(event.currentTarget)}
              endIcon={<KeyboardArrowDownRounded sx={{ fontSize: 20, transform: portfolioMenuAnchor ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 140ms ease' }} />}
              sx={{
                ...headerControlSx,
                minWidth: 220,
                px: 1.25,
                justifyContent: 'space-between',
                borderColor: portfolioMenuAnchor ? '#cbd5e1' : '#e2e8f0',
                '& .MuiButton-endIcon': {
                  ml: 0.5,
                  color: '#64748b',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, minWidth: 0 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: selectedPortfolio?.is_active ? '#10b981' : '#cbd5e1', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', lineHeight: 1.1 }} noWrap>
                  {selectedPortfolio?.name || t('paperTrading.selectPlaceholder')}
                </Typography>
              </Box>
            </Button>

            <Popover
              open={Boolean(portfolioMenuAnchor)}
              anchorEl={portfolioMenuAnchor}
              onClose={() => setPortfolioMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{
                paper: {
                  elevation: 0,
                  sx: {
                    mt: 0.75,
                    width: portfolioMenuAnchor?.clientWidth || 220,
                    borderRadius: '4px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
                    p: 0.5,
                    overflow: 'hidden',
                  },
                },
              }}
            >
              <Box sx={{ display: 'grid', gap: 0.5 }}>
                {portfolios.map((item) => {
                  const isSelected = item.id === portfolioId;
                  return (
                    <MenuItem
                      key={item.id}
                      selected={isSelected}
                      onClick={() => {
                        setPortfolioId(item.id);
                        setPortfolioMenuAnchor(null);
                      }}
                      sx={{
                        minHeight: 40,
                        borderRadius: '4px',
                        px: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        '&.Mui-selected': {
                          bgcolor: '#f8fafc',
                        },
                        '&.Mui-selected:hover': {
                          bgcolor: '#f1f5f9',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, minWidth: 0 }}>
                        <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: item.is_active ? '#10b981' : '#cbd5e1', flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: isSelected ? '#334155' : '#1e293b', lineHeight: 1.1 }} noWrap>
                            {item.name}
                          </Typography>
                          <Typography sx={{ mt: 0.2, fontSize: '0.64rem', color: item.is_active ? '#059669' : '#94a3b8', fontWeight: 700 }}>
                            {item.is_active ? t('paperTrading.status.active') : t('paperTrading.status.inactive')}
                          </Typography>
                        </Box>
                      </Box>
                      {isSelected ? <CheckRounded sx={{ fontSize: 16, color: '#475569', flexShrink: 0 }} /> : null}
                    </MenuItem>
                  );
                })}
              </Box>
            </Popover>

            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh sx={{ fontSize: 16 }} />}
              onClick={loadBoard}
              disabled={loading || !portfolioId}
              sx={{
                ...headerControlSx,
                px: 1.25,
                color: '#475569',
                '& .MuiButton-startIcon': { mr: 0.5 },
              }}
            >
              {t('paperTrading.actions.refresh')}
            </Button>
          </Box>
        </Box>

        {error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}

        {!error && !loading && !portfolioId ? (
          <Paper elevation={0} sx={{ ...surfaceSx, p: 4, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>
              {t('paperTrading.noPortfolioTitle')}
            </Typography>
            <Typography sx={{ mt: 0.8, fontSize: '0.78rem', color: '#64748b' }}>
              {t('paperTrading.noPortfolioSubtitle')}
            </Typography>
          </Paper>
        ) : null}

        {!error && portfolioId ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '320px 1fr' }, gap: 2 }}>
            <Box>
              <ModelPortfolioActions
                portfolioId={portfolioId}
                portfolio={portfolio}
                positions={positions}
                onTradeSuccess={loadBoard}
              />
            </Box>
            <Box>
              <ModelPortfolioBoard boardData={modelBoard} />
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
